import React, { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { Superscript } from '@tiptap/extension-superscript';
import { Subscript } from '@tiptap/extension-subscript';
import Placeholder from '@tiptap/extension-placeholder';
import { draftRawToHTML, tiptapHTMLToDraftRaw } from './draftTiptapConverter';
import CharacterCount from '@tiptap/extension-character-count';
import { Underline } from '@tiptap/extension-underline';
import './tiptap-editor.css'; // 👈 import the CSS
import Button from '../button';
import Input from '../inputpm';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    // Button,
    // TextField,
    IconButton,
    Tooltip,
    MenuItem,
    Select,
    Menu,
    useMediaQuery,
} from '@mui/material';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    StrikethroughS,
    FormatListBulleted,
    FormatListNumbered,
    Undo,
    Redo,
    ImageOutlined,
    FormatClear,
    Link as LinkIcon,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatAlignJustify,
    Code,
    Superscript as SuperscriptIcon,
    Subscript as SubscriptIcon,
    DeleteForever,
    MoreVert,
} from '@mui/icons-material';

export default function TiptapEditor({
    value,
    onChange,
    readOnly = false,
    placeholder = 'Enter content here...',
    maxCharLimit = null
}) {
    // console.log('check props data========', value, readOnly, placeholder, maxCharLimit);
    const lastValueRef = useRef(value);
    const html = draftRawToHTML(value);

    // dialog states
    const [showImageDialog, setShowImageDialog] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imageDims, setImageDims] = useState({ width: '', height: '' });
    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');

    // style states
    const [textColor, setTextColor] = useState('#000000');
    const [highlightColor, setHighlightColor] = useState('#ffff00');
    const [fontSize, setFontSize] = useState('16px');
    const [blockType, setBlockType] = useState('paragraph');

    // responsive
    const isMobile = useMediaQuery('(max-width: 900px)');
    const [anchorEl, setAnchorEl] = useState(null);
    const handleMoreClick = (e) => setAnchorEl(e.currentTarget);
    const handleMoreClose = () => setAnchorEl(null);

    // sticky shadow toggle
    const [scrolled, setScrolled] = useState(false);
    const contentRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(contentRef.current.scrollTop > 5);
        };
        const el = contentRef.current;
        if (el) el.addEventListener('scroll', handleScroll);
        return () => el?.removeEventListener('scroll', handleScroll);
    }, []);

    const editor = useEditor({
        extensions: [
            StarterKit,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            Superscript,
            Subscript,
            Underline,
            Image.configure({ allowBase64: true }),
            Link.configure({ openOnClick: false }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({
                placeholder: placeholder || '',
                emptyEditorClass:
                    'is-editor-empty before:content-[attr(data-placeholder)] before:text-gray-400 before:float-left before:pointer-events-none',
            }),
            CharacterCount.configure({
                limit: maxCharLimit,
            })
        ],
        editable: !readOnly,
        editable: !readOnly,
        content: html,
        editorProps: {
            attributes: {
                class:
                    'prose max-w-none p-2 outline-none min-h-[150px] focus:outline-none',
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            const raw = tiptapHTMLToDraftRaw(html);
            const json = JSON.stringify(raw);
            if (json !== lastValueRef.current) {
                lastValueRef.current = json;
                if (onChange) {
                    onChange(json);
                }
            }
        },
    });

    // Watch for readOnly changes
    useEffect(() => {
        if (editor) {
            editor.setEditable(!readOnly);
        }
    }, [readOnly, editor]);

    useEffect(() => {
        if (!editor) return;
        if (value !== lastValueRef.current) {
            lastValueRef.current = value;
            editor.commands.setContent(draftRawToHTML(value), false);
        }
    }, [value, editor]);

    // useEffect(() => {
    //     if (!editor) return;

    //     const updateBlockType = () => {
    //         const heading = editor.isActive('heading', { level: 1 })
    //             ? 'heading1'
    //             : editor.isActive('heading', { level: 2 })
    //                 ? 'heading2'
    //                 : editor.isActive('heading', { level: 3 })
    //                     ? 'heading3'
    //                     : editor.isActive('heading', { level: 4 })
    //                         ? 'heading4'
    //                         : editor.isActive('heading', { level: 5 })
    //                             ? 'heading5'
    //                             : editor.isActive('heading', { level: 6 })
    //                                 ? 'heading6'
    //                                 : 'paragraph';
    //         setBlockType(heading);
    //     };

    //     editor.on('selectionUpdate', updateBlockType);
    //     editor.on('transaction', updateBlockType);

    //     return () => {
    //         editor.off('selectionUpdate', updateBlockType);
    //         editor.off('transaction', updateBlockType);
    //     };
    // }, [editor]);

    // image upload
    const handleAddImage = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files?.[0];
            if (file) {
                setImageFile(file);
                setShowImageDialog(true);
            }
        };
        input.click();
    };

    const handleImageDialogClose = (confirm = false) => {
        setShowImageDialog(false);
        if (confirm && imageFile && editor) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64 = e.target.result;
                const { width, height } = imageDims;
                editor.chain().focus().setImage({ src: base64, width, height }).run();
            };
            reader.readAsDataURL(imageFile);
        }
        setImageFile(null);
        setImageDims({ width: '', height: '' });
    };

    // link
    const handleAddLink = () => setShowLinkDialog(true);
    const applyLink = () => {
        if (linkUrl.trim()) editor.chain().focus().setLink({ href: linkUrl }).run();
        setShowLinkDialog(false);
        setLinkUrl('');
    };
    const removeLink = () => editor.chain().focus().unsetLink().run();

    // color / alignment
    const applyTextColor = (color) => {
        setTextColor(color);
        editor.chain().focus().setColor(color).run();
    };
    const applyHighlight = (color) => {
        setHighlightColor(color);
        editor.chain().focus().toggleHighlight({ color }).run();
    };
    const applyAlignment = (align) => editor.chain().focus().setTextAlign(align).run();

    if (!editor) return <p>Loading editor...</p>;

    const toolbarItems = (
        <>
            <Tooltip title="Bold"><IconButton onClick={() => editor.chain().focus().toggleBold().run()}><FormatBold /></IconButton></Tooltip>
            <Tooltip title="Italic"><IconButton onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalic /></IconButton></Tooltip>
            <Tooltip title="Underline"><IconButton onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlined /></IconButton></Tooltip>
            <Tooltip title="Strike"><IconButton onClick={() => editor.chain().focus().toggleStrike().run()}><StrikethroughS /></IconButton></Tooltip>
            <Tooltip title="Superscript"><IconButton onClick={() => editor.chain().focus().toggleSuperscript().run()}><SuperscriptIcon /></IconButton></Tooltip>
            <Tooltip title="Subscript"><IconButton onClick={() => editor.chain().focus().toggleSubscript().run()}><SubscriptIcon /></IconButton></Tooltip>
            <Tooltip title="Monospace"><IconButton onClick={() => editor.chain().focus().toggleCode().run()}><Code /></IconButton></Tooltip>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* Headings */}
                <Tooltip title="Monospace">
                    <Select
                        size="small"
                        value={blockType}
                        onChange={(e) => {
                            const type = e.target.value;
                            setBlockType(type);
                            editor.chain().focus().clearNodes();
                            if (type === 'paragraph') editor.chain().focus().setParagraph().run();
                            else if (type.startsWith('heading')) {
                                const level = parseInt(type.replace('heading', ''));
                                editor.chain().focus().toggleHeading({ level }).run();
                            }
                        }}
                        sx={{ width: 140, height: 36 }}
                    >
                        <MenuItem value="paragraph">Paragraph</MenuItem>
                        <MenuItem value="heading1">Heading 1</MenuItem>
                        <MenuItem value="heading2">Heading 2</MenuItem>
                        <MenuItem value="heading3">Heading 3</MenuItem>
                        <MenuItem value="heading4">Heading 4</MenuItem>
                        <MenuItem value="heading5">Heading 5</MenuItem>
                        <MenuItem value="heading6">Heading 6</MenuItem>
                    </Select>
                </Tooltip>

                {/* Font Size */}
                <Tooltip title="Monospace">
                    <Select
                        size="small"
                        value={fontSize}
                        onChange={(e) => {
                            const size = e.target.value;
                            setFontSize(size);
                            editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
                        }}
                        sx={{ width: 80, height: 36 }}
                    >
                        {['12px', '14px', '16px', '18px', '20px', '24px', '28px'].map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </Tooltip>
            </div>

            <Tooltip title="Unordered List"><IconButton onClick={() => editor.chain().focus().toggleBulletList().run()}><FormatListBulleted /></IconButton></Tooltip>
            <Tooltip title="Ordered List"><IconButton onClick={() => editor.chain().focus().toggleOrderedList().run()}><FormatListNumbered /></IconButton></Tooltip>
            <Tooltip title="Align Left"><IconButton onClick={() => applyAlignment('left')}><FormatAlignLeft /></IconButton></Tooltip>
            <Tooltip title="Align Center"><IconButton onClick={() => applyAlignment('center')}><FormatAlignCenter /></IconButton></Tooltip>
            <Tooltip title="Align Right"><IconButton onClick={() => applyAlignment('right')}><FormatAlignRight /></IconButton></Tooltip>
            <Tooltip title="Justify"><IconButton onClick={() => applyAlignment('justify')}><FormatAlignJustify /></IconButton></Tooltip>
            <input type="color" value={textColor} onChange={(e) => applyTextColor(e.target.value)} title="Text color" style={{ cursor: 'pointer' }} />
            <input type="color" value={highlightColor} onChange={(e) => applyHighlight(e.target.value)} title="Highlight" style={{ cursor: 'pointer' }} />
            <Tooltip title="Add Link"><IconButton onClick={handleAddLink}><LinkIcon /></IconButton></Tooltip>
            <Tooltip title="Remove Link"><IconButton onClick={removeLink}><LinkIcon color="error" /></IconButton></Tooltip>
            <Tooltip title="Image"><IconButton onClick={handleAddImage}><ImageOutlined /></IconButton></Tooltip>
            <Tooltip title="Undo"><IconButton onClick={() => editor.chain().focus().undo().run()}><Undo /></IconButton></Tooltip>
            <Tooltip title="Redo"><IconButton onClick={() => editor.chain().focus().redo().run()}><Redo /></IconButton></Tooltip>
            <Tooltip title="Clear Formatting"><IconButton onClick={() => editor.chain().focus().unsetAllMarks().run()}><FormatClear /></IconButton></Tooltip>
            <Tooltip title="Clear All"><IconButton onClick={() => editor.chain().focus().clearContent(true).run()}><DeleteForever /></IconButton></Tooltip>
        </>
    );

    return (
        <div className={readOnly ? 'tiptap-editor-container-readonly' : 'tiptap-editor-container'}>
            {/* Sticky Toolbar */}
            {!readOnly && (
                <div className={`tiptap-toolbar ${scrolled ? 'scrolled' : ''}`}>
                    {isMobile ? (
                        <>
                            <Tooltip title="More">
                                <IconButton onClick={handleMoreClick}><MoreVert /></IconButton>
                            </Tooltip>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMoreClose}>
                                <div style={{ padding: 4, display: 'flex', flexWrap: 'wrap', maxWidth: 350 }}>
                                    {toolbarItems}
                                </div>
                            </Menu>
                        </>
                    ) : (
                        toolbarItems
                    )}
                </div>
            )}

            {/* Scrollable Content */}
            <div className="tiptap-content" ref={contentRef}>
                <EditorContent editor={editor} />
                {!readOnly && maxCharLimit && (
                    <div
                        style={{
                            textAlign: 'right',
                            fontSize: 13,
                            marginTop: 6,
                            color:
                                editor.storage.characterCount.characters() > maxCharLimit
                                    ? '#ff4444'
                                    : editor.storage.characterCount.characters() > maxCharLimit * 0.8
                                        ? '#f6a623'
                                        : '#666',
                            transition: 'color 0.2s ease',
                        }}
                    >
                        {editor.storage.characterCount.characters()}/{maxCharLimit} characters
                        {'  •  '}
                        {Math.max(0, maxCharLimit - editor.storage.characterCount.characters())} remaining
                    </div>
                )}
            </div>

            {/* Image Dialog */}
            <Dialog open={showImageDialog} onClose={() => handleImageDialogClose(false)} disableScrollLock fullWidth={true} maxWidth={'xs'}>
                <DialogTitle>Insert Image</DialogTitle>
                <DialogContent dividers>
                    <Input
                        placeholder="Width (px)"
                        fullWidth
                        margin="dense"
                        value={imageDims.width}
                        onChange={(e) => setImageDims({ ...imageDims, width: e.target.value })}
                        style={{ marginBottom: '14px' }}
                    />
                    <Input
                        placeholder="Height (px)"
                        fullWidth
                        margin="dense"
                        value={imageDims.height}
                        onChange={(e) => setImageDims({ ...imageDims, height: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleImageDialogClose(false)} className="buttonSecondary">Cancel</Button>
                    <Button onClick={() => handleImageDialogClose(true)} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Link Dialog */}
            <Dialog open={showLinkDialog} onClose={() => setShowLinkDialog(false)} disableScrollLock fullWidth={true} maxWidth={'xs'}>
                <DialogTitle>Insert Link</DialogTitle>
                <DialogContent dividers>
                    <Input
                        placeholder="URL"
                        fullWidth
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowLinkDialog(false)} className="buttonSecondary">Cancel</Button>
                    <Button onClick={applyLink} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
